export type ScreenPoint = { x: number; y: number };
export type ScreenFrame = ScreenPoint & { width: number; height: number };
/** What `hp:selection-frame` carries: the object's frame and the parts of it the bar must leave free (a handle's touch zone). */
export type SelectionFrame = ScreenFrame & { keepOut?: ScreenFrame[] };
export interface ContextPlacementInput {
  anchor: ScreenPoint | null;
  frame: ScreenFrame | null;
  bar: { width: number; height: number };
  viewport: { width: number; height: number };
  keepOut?: ScreenFrame[];
}

export function contextBarPosition({ anchor, frame, bar, viewport, keepOut = [] }: ContextPlacementInput) {
  // The explicit web interaction places chrome at the hit; native bounds are only
  // a fallback for keyboard/programmatic selection with no pointer anchor.
  const bounds = anchor ? { ...anchor, width: 0, height: 0 }
    : frame ?? { x: viewport.width / 2, y: viewport.height / 2, width: 0, height: 0 };
  const point = anchor ?? { x: bounds.x + bounds.width / 2, y: bounds.y + bounds.height / 2 };
  const gap = 14, margin = 10;
  const candidates = [
    { left: point.x - bar.width / 2, top: bounds.y - bar.height - gap },
    { left: point.x - bar.width / 2, top: bounds.y + bounds.height + gap },
    { left: bounds.x - bar.width - gap, top: point.y - bar.height / 2 },
    { left: bounds.x + bounds.width + gap, top: point.y - bar.height / 2 },
  ];
  // Nearby chrome edges are valid alternatives, without a hard-coded toolbar height. The
  // free strip often needs one panel's edge horizontally and another's vertically — a
  // right-hand inspector plus the construction rail leave nothing else on a tablet — so
  // every edge is offered on both axes and the combinations are scored too.
  const lefts = [point.x - bar.width / 2, bounds.x - bar.width - gap, bounds.x + bounds.width + gap,
    ...keepOut.flatMap(obstacle => [obstacle.x - bar.width - 8, obstacle.x + obstacle.width + 8])];
  const tops = [bounds.y - bar.height - gap, bounds.y + bounds.height + gap, point.y - bar.height / 2,
    ...keepOut.flatMap(obstacle => [obstacle.y - bar.height - 8, obstacle.y + obstacle.height + 8])];
  for (const left of lefts) for (const top of tops) candidates.push({ left, top });
  const clamp = (n: number, max: number) => Math.max(margin, Math.min(n, Math.max(margin, max - margin)));
  const overlap = (a: ScreenFrame, b: ScreenFrame) => Math.max(0, Math.min(a.x + a.width, b.x + b.width) - Math.max(a.x, b.x)) * Math.max(0, Math.min(a.y + a.height, b.y + b.height) - Math.max(a.y, b.y));
  const scored = candidates.map((candidate, order) => {
    const left = clamp(candidate.left, viewport.width - bar.width), top = clamp(candidate.top, viewport.height - bar.height);
    const rect = { x: left, y: top, width: bar.width, height: bar.height };
    const dx = Math.max(left - point.x, point.x - left - bar.width, 0), dy = Math.max(top - point.y, point.y - top - bar.height, 0);
    const distance = Math.hypot(dx, dy);
    const collision = keepOut.reduce((sum, obstacle) => sum + overlap(rect, { x: obstacle.x - 8, y: obstacle.y - 8, width: obstacle.width + 16, height: obstacle.height + 16 }), 0);
    return { left, top, score: collision * 1e6 + Math.max(0, gap - distance) * 1e6 + distance + order * .001 };
  });
  scored.sort((a, b) => a.score - b.score);
  return { left: scored[0].left, top: scored[0].top };
}
